/**
 * src/services/leaderboardService.ts
 *
 * Supabase query wrappers + TypeScript types for the leaderboards panel UI.
 * Two top-level fetches:
 *   - fetchGlobalPoints(limit): top-N points leaders from
 *     `public.v_leaderboard_global_points`, joined to `public.profiles` for the
 *     display name + photo URL.
 *   - fetchPerGame(gameId, limit): top-N rows from
 *     `public.v_leaderboard_per_game` for a specific game_id, metric-aware
 *     sort (ASC for time_ms, DESC for score / rating / win_loss_draw) and
 *     bucketed per metric.
 *
 * Both fetches gracefully fail to a thrown Error on supabase RPC errors so
 * the panel can render an explicit error state instead of a silent empty
 * list. The current user's row is NOT pinned separately — the panel surfaces
 * a "YOU" badge inline once the main fetch returns.
 */
import { supabase } from '../supabase';

export type LeaderboardMetric = 'score' | 'time_ms' | 'win_loss_draw' | 'rating';

export interface GlobalPointsRow {
  userId: string;
  displayName: string;
  photoUrl: string | null;
  totalPoints: number;
}

export interface PerGameRow {
  userId: string;
  displayName: string;
  photoUrl: string | null;
  metric: LeaderboardMetric;
  score: number;
  timeMs: number | null;
  achievedAt: string;
  // Outcome breakdown (populated by v_leaderboard_per_game for win_loss_draw
  // rows; null/0 for score and time_ms rows). The leaderboards panel renders
  // losses/draws/gamesPlayed as a small subtitle on chess rows so W/L/D are
  // visible alongside the rank.
  losses?: number;
  draws?: number;
  gamesPlayed?: number;
  lastPlayedAt?: string | null;
}

interface ProfileLookup {
  displayName: string;
  photoUrl: string | null;
}

async function loadProfiles(userIds: string[]): Promise<Map<string, ProfileLookup>> {
  if (userIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, photo_url')
    .in('id', userIds);
  if (error) {
    throw new Error(`Failed to load profiles: ${error.message}`);
  }
  const map = new Map<string, ProfileLookup>();
  for (const p of (data ?? []) as Array<{ id: string; display_name: string | null; photo_url: string | null }>) {
    map.set(p.id, {
      displayName: p.display_name ?? 'Unknown',
      photoUrl: p.photo_url ?? null,
    });
  }
  return map;
}

/**
 * Top-N by total score points summed across all metric='score' rows per
 * user. The view does the aggregation server-side, the profile lookup is
 * one extra query, and the join is in-process.
 */
export async function fetchGlobalPoints(limit = 50): Promise<GlobalPointsRow[]> {
  const { data, error } = await supabase
    .from('v_leaderboard_global_points')
    .select('user_id, total_score_points')
    .order('total_score_points', { ascending: false })
    .limit(limit);
  if (error) {
    throw new Error(`Failed to fetch global points: ${error.message}`);
  }
  const ids = (data ?? []).map((r: { user_id: string }) => r.user_id);
  const profiles = await loadProfiles(ids);
  return (data ?? []).map((r: { user_id: string; total_score_points: number | string }) => {
    const profile = profiles.get(r.user_id);
    return {
      userId: r.user_id,
      displayName: profile?.displayName ?? 'Unknown',
      photoUrl: profile?.photoUrl ?? null,
      totalPoints: Number(r.total_score_points) || 0,
    };
  });
}

/**
 * Per-game top-N grouped by metric. For time_ms metrics we sort ASC
 * (lower is better); for everything else DESC. We over-fetch so the
 * per-metric top-N is accurate even when the view returns hundreds of
 * rows for active games. Returns rows pre-flattened with each row's
 * metric stamp intact so the UI can bucket them visually.
 */
export async function fetchPerGame(gameId: string, limit = 50): Promise<PerGameRow[]> {
  if (!gameId) return [];
  const { data, error } = await supabase
    .from('v_leaderboard_per_game')
    .select('user_id, metric, score, time_ms, achieved_at, losses, draws, games_played, last_played_at')
    .eq('game_id', gameId)
    .limit(limit * 3); // overfetch; per-metric sort + slice below
  if (error) {
    throw new Error(`Failed to fetch per-game leaderboard: ${error.message}`);
  }
  const ids = (data ?? []).map((r: { user_id: string }) => r.user_id);
  const profiles = await loadProfiles(ids);

  const grouped = new Map<LeaderboardMetric, PerGameRow[]>();
  for (const r of (data ?? []) as Array<{
    user_id: string;
    metric: LeaderboardMetric;
    score: number | string | null;
    time_ms: number | null;
    achieved_at: string;
    losses?: number | string | null;
    draws?: number | string | null;
    games_played?: number | string | null;
    last_played_at?: string | null;
  }>) {
    const profile = profiles.get(r.user_id);
    const row: PerGameRow = {
      userId: r.user_id,
      displayName: profile?.displayName ?? 'Unknown',
      photoUrl: profile?.photoUrl ?? null,
      metric: r.metric,
      score: Number(r.score) || 0,
      timeMs: r.time_ms ?? null,
      achievedAt: r.achieved_at,
      losses: Number(r.losses ?? 0) || 0,
      draws: Number(r.draws ?? 0) || 0,
      gamesPlayed: Number(r.games_played ?? 0) || 0,
      lastPlayedAt: r.last_played_at ?? null,
    };
    const bucket = grouped.get(row.metric) ?? [];
    bucket.push(row);
    grouped.set(row.metric, bucket);
  }

  const flat: PerGameRow[] = [];
  for (const [metric, rows] of grouped) {
    rows.sort((a, b) =>
      metric === 'time_ms'
        ? (a.timeMs ?? Number.MAX_SAFE_INTEGER) - (b.timeMs ?? Number.MAX_SAFE_INTEGER)
        : b.score - a.score,
    );
    flat.push(...rows.slice(0, limit));
  }
  return flat;
}

/**
 * record_chess_result — write a chess match outcome to public.leaderboards
 * for both participants atomically (wins/losses/draws/games_played).
 *
 * The underlying RPC `public.record_chess_result(...)` is SECURITY DEFINER
 * with two upserts in one function call. Idempotency lives in the DB:
 * `last_match_key` matches suppress the on-conflict update, so both peers
 * CAN call this for the same match and the second is a no-op.
 *
 * Caller-supplied `matchKey` MUST be derived so both peers arrive at the
 * same key for the same match — CyberChessActivity uses
 * `${session.id}::${syncState.gameOver}` for exactly that reason.
 *
 * Best-effort on the caller: throws on RPC error so the activity can wrap
 * in try/catch and surface a console warning + swallow, never crash the
 * chess UI when leaderboards are momentarily unreachable.
 */
export interface RecordChessResultArgs {
  whiteUserId: string;
  blackUserId: string;
  winnerUserId: string | null;
  isDraw: boolean;
  matchKey: string;
  durationSeconds?: number | null;
}

export async function recordChessResult(args: RecordChessResultArgs): Promise<void> {
  if (!args.whiteUserId || !args.blackUserId) {
    throw new Error('recordChessResult: both whiteUserId and blackUserId are required');
  }
  if (!args.isDraw && !args.winnerUserId) {
    throw new Error('recordChessResult: non-draw match requires winnerUserId');
  }
  if (args.whiteUserId === args.blackUserId) {
    throw new Error('recordChessResult: white and black must differ');
  }
  if (!args.matchKey || args.matchKey.length === 0 || args.matchKey.length > 256) {
    throw new Error('recordChessResult: matchKey must be 1..256 chars');
  }
  const { error } = await supabase.rpc('record_chess_result', {
    p_player_a: args.whiteUserId,
    p_player_b: args.blackUserId,
    p_winner: args.winnerUserId,
    p_is_draw: args.isDraw,
    p_match_key: args.matchKey,
    p_duration_sec: args.durationSeconds ?? null,
  });
  if (error) {
    throw new Error(`recordChessResult RPC failed: ${error.message}`);
  }
}
